import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { ProgressBar, Spinner } from '@/shared/ui';
import type { WorklistRow } from '../api/verificationApi';
import { formatDateTime, intakeLabel } from '../utils/format';
import { SourceChips, StatusPill } from './chips';
import { RevealDob } from './RevealDob';
import { IV_BASE } from './IvSubnav';
import styles from './WorklistTable.module.css';

interface WorklistTableProps {
  rows: WorklistRow[];
  isLoading: boolean;
}

export function WorklistTable({ rows, isLoading }: WorklistTableProps) {
  const navigate = useNavigate();

  return (
    <div className={patterns.gridTable}>
      <div className={clsx(patterns.gridTableHeader, styles.cols)}>
        <span>Patient</span>
        <span>Appointment</span>
        <span>Carrier / plan</span>
        <span>Status</span>
        <span>Completeness</span>
        <span />
      </div>

      {isLoading && rows.length === 0 && (
        <div className={patterns.gridTableEmpty}>
          <Spinner size="sm" />
        </div>
      )}

      {!isLoading && rows.length === 0 && (
        <div className={patterns.gridTableEmpty}>No appointments match this filter.</div>
      )}

      {rows.map((row) => {
        const planLine = [row.planType, row.groupName].filter(Boolean).join(' · ');
        const apptLine = [
          row.appointmentAt ? formatDateTime(row.appointmentAt) : null,
          intakeLabel(row.intakeSource),
        ]
          .filter(Boolean)
          .join(' · ');
        const href = `${IV_BASE}/verifications/${row.id}`;

        return (
          <div
            key={row.id}
            role="link"
            tabIndex={0}
            className={clsx(patterns.gridTableRow, styles.cols, styles.row)}
            onClick={() => navigate(href)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(href);
              }
            }}
          >
            <div>
              <div className={patterns.cellPrimary}>{row.patientName}</div>
              <div className={patterns.cellSmMuted}>
                DOB <RevealDob verificationId={row.id} masked={row.patientDobMasked} /> ·{' '}
                {row.memberIdMasked}
              </div>
            </div>
            <div>
              <div className={patterns.cellSecondary}>{row.appointmentTypeLabel}</div>
              <div className={patterns.cellSmMuted}>{apptLine}</div>
            </div>
            <div>
              <div className={patterns.cellSecondary}>{row.carrierName}</div>
              {planLine && <div className={patterns.cellSmMuted}>{planLine}</div>}
            </div>
            <div>
              <StatusPill status={row.status} failureCode={row.failureCode} />
            </div>
            <div>
              {row.completeness ? (
                <div className={styles.progress}>
                  <ProgressBar
                    value={row.completeness.met}
                    max={row.completeness.total}
                    height={6}
                    className={styles.progressBar}
                  />
                  <span className={styles.progressNum}>
                    {row.completeness.met}/{row.completeness.total}
                  </span>
                </div>
              ) : (
                <span className={patterns.cellSmMuted}>—</span>
              )}
              <div className={styles.sources}>
                <SourceChips sources={row.sources} />
              </div>
            </div>
            <div className={styles.caret}>
              <ChevronRight size={16} aria-hidden />
            </div>
          </div>
        );
      })}
    </div>
  );
}
